// src/components/UmatUpdateIndividual.js
import React, { useEffect, useState } from "react";
import LookupUmat from "./LookupUmat";
import DropDownArea from "./DropDownArea";
import DropDownAgeGroup from "./DropDownAgeGroup";
import { apiFetch, getUmatByEmail } from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import PERMISSIONS, { getGlobalVariables } from "../utils/wkCommon";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import DropDownEditableEnum from "./DropDownEditableEnum";

/**
 * UmatUpdateIndividual (permission-aware)
 *
 * - UMATDATA_EDIT -> create umat-updates (Pending)
 * - UMATDATA_EDITAPPROVAL -> apply change immediately (PUT /api/umats/{id}) and create umat-updates (Applied)
 *
 * Minimal modification: added FIELD_LABELS so each field can show user-friendly label.
 */

/* ----------------- KEEP THESE (existing constants) ----------------- */
const TWO_ROW_FIELDS = new Set([
  "notes",
  "address",
  "hobbiesInterests",
  "father_umat_item",
  "mother_umat_item",
  "spouseId",
]);

const EXCLUDE = new Set([
  "umatID","teamPelaksana","eventParticipations","profilepic","avatarpic","user",
  "LoginEmail","LoginPIN","location_full_slug","umat_roles","agenda_pre_registrations",
  "regions","qiu_dao_person","recordOwner","recordEditor","areas","agenda_book",
  "umat_relation1","umat_relation2","relation_contact_preference"
]);

const ALLOWED_FIELDS = [
  "nickName","fullName","chineseName","pinyinName","displayName","gender",
  "mobilePhone","homePhone","birthdate","district","email","address",
  "fatherName","motherName","hobbiesInterests","education","occupation","notes",
  "area",/*"category_age_groups",*/"father_umat_item","mother_umat_item", "spouseId"
].filter(f => !EXCLUDE.has(f));

function fieldTypeOf(field) {
  const dateFields = new Set(["birthdate"]);
  if (!field) return "string";
  if (field === "gender") return "gender";
  if (field === "area") return "area";
  if (field === "category_age_groups") return "category_age_groups";
  if (field === "father_umat_item" || field === "mother_umat_item" || field === "spouseId") return "lookupUmat";
  if (dateFields.has(field)) return "date";
  return "string";
}

/* ----------------- NEW: field labels (customize here) ----------------- */
/* Edit/extend FIELD_LABELS to change how fields appear to the user.
   Keys must match the real field keys (those used as values in <option>).
*/
const FIELD_LABELS = {
  nickName: "Nama Panggilan",
  fullName: "Nama Lengkap",
  chineseName: "Nama Tionghoa",
  pinyinName: "Nama (Pinyin)",
  displayName: "Nama Tampil",
  gender: "Jenis Kelamin",
  mobilePhone: "HP / Mobile",
  homePhone: "Telepon Rumah",
  birthdate: "Tanggal Lahir",
  district: "Distrik",
  email: "Email",
  address: "Alamat",
  fatherName: "Nama Ayah",
  motherName: "Nama Ibu",
  hobbiesInterests: "Hobi & Minat",
  education: "Pendidikan",
  occupation: "Pekerjaan",
  notes: "Catatan",
  area: "Area / Wilayah",
  category_age_groups: "Kelompok Usia",
  father_umat_item: "Orangtua (Ayah) - pilih dari data umat",
  mother_umat_item: "Orangtua (Ibu) - pilih dari data umat",
  spouseId: "Pasangan (Suami / Istri)",
};

function prettifyFieldName(field) {
  if (!field) return "";
  const s = field.replace(/([A-Z])/g, " $1").replace(/[_\-]+/g, " ");
  return s.replace(/\b\w/g, ch => ch.toUpperCase()).trim();
}

/* ----------------- component (original logic kept) ----------------- */
export default function UmatUpdateIndividual({ onSaved, umatId }) {
  const [selectedUmat, setSelectedUmat] = useState(null);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }
  const [canEdit, setCanEdit] = useState(false);
  const [canEditAndApply, setCanEditAndApply] = useState(false);
  const [lockedKodeUmat, setLockedKodeUmat] = useState(null);
  const user = getUser();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [educationTemplates, setEducationTemplates] = useState([]);
  const [occupationTemplates, setOccupationTemplates] = useState([]);

  useEffect(() => {
    async function loadEnumTemplates() {
      try {
        const vars = await getGlobalVariables([
          "EDUCATION_TEMPLATES",
          "OCCUPATION_TEMPLATES"
        ]);
        setEducationTemplates(vars.EDUCATION_TEMPLATES || []);
        setOccupationTemplates(vars.OCCUPATION_TEMPLATES || []);
      } catch (err) {
        console.warn("load enum templates failed", err);
      }
    }
    loadEnumTemplates();
  }, []);

  useEffect(() => {
    if (!umatId) return;

    async function loadUmat() {
      try {
        const resp = await apiFetch(`/api/umats/${umatId}?populate=area`);
        const u = resp?.data ?? null;

        if (!u) return;

        const normalized = u.attributes
          ? { id: u.id, ...u.attributes }
          : u;

        setLockedKodeUmat(normalized.umatID ?? umatId);
        setSelectedUmat(normalized);

      } catch (err) {
        console.error("load umat failed", err);
      }
    }

    loadUmat();

  }, [umatId]);

  // load permissions (follow Home.js pattern: use hasUmatPermissionAsync)
  useEffect(() => {
    let mounted = true;
    async function loadPerms() {
      try {
        const email = user?.email;
        if (!email) {
          if (mounted) { setCanEdit(false); setCanEditAndApply(false); }
          return;
        }
        const pEdit = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDIT);
        const pEditApproval = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDITAPPROVAL);
        if (mounted) {
          setCanEdit(Boolean(pEdit));
          setCanEditAndApply(Boolean(pEditApproval));
        }
      } catch (err) {
        console.warn("loadPerms error", err);
        if (mounted) { setCanEdit(false); setCanEditAndApply(false); }
      }
    }
    loadPerms();
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    setRows([]);
    setError(null);
    setMessage(null);
  }, [selectedUmat]);

  async function updateSpousePair(currentUmat, newSpouseId) {
    if (!currentUmat?.documentId) {
      throw new Error("Invalid current umat");
    }

    const myId = currentUmat.documentId;

    if (!newSpouseId) {
      // ===== CLEAR SPOUSE =====
      if (currentUmat.spouseId) {
        console.log("updateSpousePair.myId", `/api/umats/${myId}`)
        await apiFetch(`/api/umats/${myId}`, {
          method: "PUT",
          body: { data: { spouseId: null } }
        });

        console.log("updateSpousePair.spouseId", `/api/umats/${currentUmat.spouseId}`)
        await apiFetch(`/api/umats/${currentUmat.spouseId}`, {
          method: "PUT",
          body: { data: { spouseId: null } }
        });
      }
      return;
    }

    if (String(newSpouseId) === String(myId)) {
      throw new Error("Tidak boleh memilih diri sendiri sebagai pasangan");
    }

    // ===== LOAD TARGET =====
    console.log("updateSpousePair.newSpouseId", `/api/umats/${newSpouseId}`)
    const resp = await apiFetch(`/api/umats/${newSpouseId}`);
    const target = resp?.data || resp;

    if (!target) throw new Error("Pasangan tidak ditemukan");

    const targetId = target.documentId || target.id;

    // ===== VALIDASI =====
    if (target.spouseId && String(target.spouseId) !== String(myId)) {
      throw new Error("Orang ini sudah memiliki pasangan lain");
    }

    // ===== CLEAR OLD SPOUSE (CURRENT) =====
    if (currentUmat.spouseId && String(currentUmat.spouseId) !== String(newSpouseId)) {
      console.log("updateSpousePair.currentUmat.spouseId", `/api/umats/${currentUmat.spouseId}`)
      await apiFetch(`/api/umats/${currentUmat.spouseId}`, {
        method: "PUT",
        body: { data: { spouseId: null } }
      });
    }

    // ===== CLEAR OLD SPOUSE (TARGET) =====
    if (target.spouseId && String(target.spouseId) !== String(myId)) {
      console.log("updateSpousePair.targetId", `/api/umats/${targetId}`)
      await apiFetch(`/api/umats/${targetId}`, {
        method: "PUT",
        body: { data: { spouseId: null } }
      });
    }

    // ===== SET BOTH SIDE =====
    await apiFetch(`/api/umats/${myId}`, {
      method: "PUT",
      body: { data: { spouseId: targetId } }
    });

    await apiFetch(`/api/umats/${targetId}`, {
      method: "PUT",
      body: { data: { spouseId: myId } }
    });
  }

  function addRow() { setRows(r => [...r, { id: Date.now(), field: "", value: null }]); }
  function removeRow(id) { setRows(r => r.filter(x => x.id !== id)); }
  function updateRow(id, patch) { setRows(r => r.map(x => x.id === id ? { ...x, ...patch } : x)); }

  function renderValueInput(row) {
    const type = fieldTypeOf(row.field);
    const baseStyle = { padding: "0.5rem", fontSize: "1rem", borderRadius: 6, width: "95%" };
    if (!row.field) return <div style={{ color: "#666" }}>Pilih field dulu</div>;
    if (type === "gender") {
      return (
        <select value={row.value ?? ""} onChange={(e) => updateRow(row.id, { value: e.target.value })} style={baseStyle}>
          <option value="">-- pilih gender --</option>
          <option value="乾 Pria">乾 Pria</option>
          <option value="坤 Wanita">坤 Wanita</option>
        </select>
      );
    }
    if (type === "area") {
      return <DropDownArea value={row.value ?? ""} onChange={(id)=>updateRow(row.id,{value:id})} />;
    }
    if (type === "category_age_groups") {
      return <DropDownAgeGroup value={row.value ?? ""} onChange={(id)=>updateRow(row.id,{value:id})} />;
    }
    if (type === "lookupUmat") {
      return (
        <LookupUmat
          value={row.value}
          onChange={(it) => {
            updateRow(row.id, { value: it });
          }}
          multiple={false}
          includePassedAway={true}
        />
        /*
        <LookupUmat
          value={row.value ? [row.value] : []}
          onChange={(items) => {
            const it = Array.isArray(items) && items.length > 0 ? items[0] : null;
            updateRow(row.id, { value: it });
          }}
          single={true}
        />
        */
      );
    }
    if (row.field === "education") {
      return (
        <DropDownEditableEnum
          value={row.value ?? ""}
          onChange={(v)=>updateRow(row.id,{value:v})}
          options={educationTemplates}
          placeholder="Pilih atau ketik pendidikan"
        />
      );
    }    
    if (row.field === "occupation") {
      return (
        <DropDownEditableEnum
          value={row.value ?? ""}
          onChange={(v)=>updateRow(row.id,{value:v})}
          options={occupationTemplates}
          placeholder="Pilih atau ketik pekerjaan"
        />
      );
    }
    if (type === "date") {
      return <input type="date" value={row.value ?? ""} onChange={(e)=>updateRow(row.id,{value:e.target.value})} style={baseStyle} />;
    }
    if (row.field === "notes" || row.field === "hobbiesInterests" || row.field === "address") {
      return <textarea value={row.value ?? ""} onChange={(e)=>updateRow(row.id,{value:e.target.value})} style={{...baseStyle, minHeight: '5rem'}} />;
    }
    return <input type="text" value={row.value ?? ""} onChange={(e)=>updateRow(row.id,{value:e.target.value})} style={baseStyle} />;
  }

  // build changes object used for both apply and payload
  function buildChanges() {
    const changes = {};
    for (const r of rows) {
      if (!r.field) continue;
      const type = fieldTypeOf(r.field);
      if (type === "area") {
        changes["area"] = r.value ? r.value : null;
      } else if (type === "category_age_groups") {
        const id = r.value ? (r.value.id ?? r.value) : null;
        if (!id) continue;
        changes["category_age_groups"] = Array.from(new Set([...(changes["category_age_groups"]||[]), Number(id)]));
      } else if (type === "lookupUmat") {
        const it = r.value;

        let id = null;

        if (it) {
          if (r.field === "spouseId") {
            // ✅ KHUSUS spouse: WAJIB documentId
            id = it.documentId ?? null;
          } else {
            // ✅ field lain tetap backward compatible
            id = it.id ?? it.documentId ?? null;
          }
        }

        if (id !== null) {
          changes[r.field] = id;
        }
      } else if (type === "gender") {
        changes[r.field] = r.value || null;
      } else if (type === "date") {
        changes[r.field] = r.value || null;
      } else {
        changes[r.field] = r.value ?? null;
      }
    }
    return changes;
  }

  async function createUmatUpdateRecord(opts = {}) {
    // opts: { type: 'single'|'bulk'|'mappingQiuDao', updateStatus: 'Pending'|'Applied'..., payload, targetUmats: [ids], appliedStatus?, notes? }
    const body = {
      data: {
        type: opts.type || "single",
        updateStatus: opts.updateStatus || "Pending",
        targetUmats: opts.targetUmats || [],
        updatePayload: opts.payload || {},
        appliedStatus: opts.appliedStatus || null,
        originalValues: opts.originalValues || null,
        notes: opts.notes || null,
        recordOwner: opts.recordOwner ?? (user?.username ?? null),
        recordEditor: opts.recordEditor ?? (user?.username ?? null),
        recordApprover: opts.recordApprover ?? null
      }
    };
    return await apiFetch("/api/umat-updates", { method: "POST", body });
  }

  async function handleSubmit(e) {
    e && e.preventDefault();
    setError(null);
    setMessage(null);

    if (!selectedUmat) return setError("Pilih umat terlebih dahulu.");
    if (!rows || rows.length === 0) return setError("Tambahkan minimal satu perubahan.");
    if (!canEdit && !canEditAndApply) {
      return setError("Anda tidak memiliki hak untuk melakukan perubahan.");
    }

    const docId = selectedUmat.documentId ?? null;
    if (!docId) return setError("Selected umat tidak memiliki id/documentId.");

    const changes = buildChanges();
    if (!changes || Object.keys(changes).length === 0) return setError("Tidak ada perubahan valid.");

    const spouseId = changes.spouseId;
    delete changes.spouseId;

    setSubmitting(true);
    
    try {
      if (canEditAndApply) {
        // User can approve & apply immediately.
        // 1) Attempt to PUT to /api/umats/{id}
        let appliedResult = { success: false, detail: null };
        try {
          let resp; // 1. update field biasa dulu
          if (Object.keys(changes).length > 0) {
            resp = await apiFetch(`/api/umats/${docId}`, {
              method: "PUT",
              body: { data: changes }
            });
          }

          // 2. handle spouse khusus
          if (typeof spouseId !== "undefined") {
            await updateSpousePair(selectedUmat, spouseId);
          }

          const hasNormalUpdate = Object.keys(changes).length > 0;
          const hasSpouseUpdate = typeof spouseId !== "undefined";

          if (
            (hasNormalUpdate && (!resp || resp.error)) ||
            (!hasNormalUpdate && !hasSpouseUpdate)
          ) {
            throw new Error("Apply failed - invalid response");
          }
          
          appliedResult = { success: true, detail: "Applied to Umat successfully" };
        } catch (applyErr) {
          appliedResult = {
            success: false,
            detail: applyErr?.message ?? String(applyErr)
          };
        }

        // 2) create umat-updates record reflecting applied result
        const createResp = await createUmatUpdateRecord({
          type: "single",
          updateStatus: appliedResult.success ? "Applied" : "Pending",
          targetUmats: [docId],
          payload: changes,
          appliedStatus: appliedResult,
          originalValues: null,
          notes: appliedResult.success ? "Applied by approver" : `Apply failed: ${appliedResult.detail}`,
          recordOwner: user?.username ?? null,
          recordEditor: user?.username ?? null,
          recordApprover: user?.username ?? null
        });

        if (appliedResult.success) {
          setMessage({ type: "success", text: "Perubahan berhasil diterapkan dan disimpan (Applied)." });
          setRows([]);
          onSaved && onSaved(createResp);
        } else {
          // apply failed but we saved umat-updates so approver/admin can retry
          setMessage({ type: "error", text: `Gagal menerapkan perubahan: ${appliedResult.detail}. Request tetap disimpan sebagai pending.` });
        }
      } else if (canEdit) {
        // User can only submit for approval -> create umat-updates with Pending
        const createResp = await createUmatUpdateRecord({
          type: "single",
          updateStatus: "Pending",
          targetUmats: [docId],
          payload: changes,
          notes: "Submitted by user (pending approval)",
          recordOwner: user?.username ?? null,
          recordEditor: user?.username ?? null
        });
        setMessage({ type: "success", text: "Permintaan perubahan tersimpan dan menunggu approval." });
        setRows([]);
        onSaved && onSaved(createResp);
      } else {
        setError("Tidak ada permission untuk submit.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setError(err?.message ?? String(err));
      setMessage({ type: "error", text: "Gagal menyimpan perubahan: " + (err?.message ?? String(err)) });
    } finally {
      setSubmitting(false);
    }
  }
    
    // Unified sizes (choose targetHeight to match Submit)
    const targetHeight = 36; // px, sesuaikan (36 biasanya cocok)
    const iconSize = 18;     // icon ukuran di dalam button

    const iconButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: `${targetHeight}px`,
    height: `${targetHeight}px`,
    padding: 0,                             // biarkan fixed size, jangan pakai vertical padding
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#ffffff",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 120ms ease",
    boxSizing: "border-box",
    };

    const iconImgStyle = {
    display: "block",   // hilangkan whitespace/line-height gap
    width: `${iconSize}px`,
    height: `${iconSize}px`,
    objectFit: "contain"
    };

    const disabledIconStyle = {
    ...iconButtonStyle,
    opacity: 0.45,
    cursor: "not-allowed",
    };

    const submitStyle = {
    background: "#2b4a84",
    color: "#fff",
    border: "none",
    height: `${targetHeight}px`,           // paksa sama tinggi
    lineHeight: `${targetHeight}px`,       // vertikal align teks
    padding: "0 12px",                      // horizontal padding tetap
    borderRadius: 6,
    cursor: submitting ? "wait" : "pointer",
    fontWeight: 600,
    fontSize: "0.95rem",
    boxSizing: "border-box"
    };

    const submitDisabledStyle = { ...submitStyle, opacity: 0.6, cursor: "not-allowed" };


  return (
    <div style={{ fontSize: "0.95rem" }}>
      <div style={{ marginBottom: "0.75rem" }}>
        <label style={{ display: "block", marginBottom: "0.375rem", color: "#2b4a84", fontWeight: 600 }}>Pilih Umat</label>
        <LookupUmat
          value={selectedUmat}
          onChange={(v) => {
            if (lockedKodeUmat) return;
            setSelectedUmat(v);
          }}
          initialValue={lockedKodeUmat ? { umatID: lockedKodeUmat } : undefined}
          disabled={!!lockedKodeUmat}
        />
        {lockedKodeUmat && <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>KodeUmat dikunci: {lockedKodeUmat}</div>}
      </div>

      {message && (
        <div style={{
          marginBottom: 12,
          padding: 10,
          borderRadius: 8,
          color: message.type === "success" ? "#0f5132" : "#842029",
          background: message.type === "success" ? "#d1e7dd" : "#f8d7da",
          border: message.type === "success" ? "1px solid #badbcc" : "1px solid #f5c2c7"
        }}>{message.text}</div>
      )}

      {error && <div style={{ color: "crimson", marginBottom: 8 }}>{error}</div>}

      {!canEdit && !canEditAndApply && (
        <div style={{ marginBottom: 12, color: "#666", background: "#fff7e6", padding: 8, borderRadius: 6, border: "1px solid #f1d6a8" }}>
          Anda tidak memiliki izin untuk submit perubahan. Hubungi admin jika perlu akses.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontWeight: 700 }}>Perubahan</div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
                type="button"
                onClick={addRow}
                disabled={!canEdit && !canEditAndApply}
                title="Add record"
                style={iconButtonStyle}
                >
                <img src="https://unpkg.com/heroicons@2.0.16/24/outline/plus.svg" alt="add" style={iconImgStyle} />
            </button>
            {rows && rows.length >= 0 && (
                <>
                <button
                    type="submit"
                    disabled={submitting || (!canEdit && !canEditAndApply)}
                    style={(submitting || (!canEdit && !canEditAndApply)) ? submitDisabledStyle : submitStyle}
                    >
                    {submitting ? "Submitting…" : (canEditAndApply ? "Apply & Save" : "Submit")}
                </button>
                </>
            ) }
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.length === 0 && <div style={{ color: "#666" }}>Belum ada perubahan. Tekan "Add" untuk menambah baris.</div>}
          {rows.map(row => {
            const isTwoRow =
              isMobile ||
              TWO_ROW_FIELDS.has(row.field) ||
              fieldTypeOf(row.field) === "lookupUmat";
              
            return (
              <div
                key={row.id}
                style={{
                  padding: 10,
                  border: "1px dashed #e6eef8",
                  borderRadius: 8,
                  display: "grid",
                  gridTemplateColumns: isTwoRow ? "1fr auto" : "1fr 2fr auto",
                  gridTemplateRows: isTwoRow ? "auto auto" : "auto",
                  gap: 10,
                  alignItems: isTwoRow ? "start" : "center"
                }}
              >
                {/* FIELD SELECTOR (label baris 1) */}
                <select
                  value={row.field ?? ""}
                  onChange={(e) =>
                    updateRow(row.id, { field: e.target.value, value: null })
                  }
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    gridColumn: isTwoRow ? "1 / -1" : "auto"
                  }}
                >
                  <option value="">-- pilih field --</option>
                  {ALLOWED_FIELDS.map(f => (
                    <option key={f} value={f}>
                      {FIELD_LABELS[f] ?? prettifyFieldName(f)}
                    </option>
                  ))}
                </select>

                {/* VALUE + DELETE */}
                {isTwoRow ? (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {renderValueInput(row)}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      disabled={!canEdit && !canEditAndApply}
                      title="Remove record"
                      style={(!canEdit && !canEditAndApply) ? disabledIconStyle : iconButtonStyle}
                    >
                      <img
                        src="https://unpkg.com/heroicons@2.0.16/24/outline/trash.svg"
                        alt="delete"
                        style={iconImgStyle}
                      />
                    </button>
                  </div>
                ) : (
                  <>
                    <div>{renderValueInput(row)}</div>

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        disabled={!canEdit && !canEditAndApply}
                        title="Remove record"
                        style={(!canEdit && !canEditAndApply) ? disabledIconStyle : iconButtonStyle}
                      >
                        <img
                          src="https://unpkg.com/heroicons@2.0.16/24/outline/trash.svg"
                          alt="delete"
                          style={iconImgStyle}
                        />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
                type="button"
                onClick={addRow}
                disabled={!canEdit && !canEditAndApply}
                title="Add record"
                style={iconButtonStyle}
                >
                <img src="https://unpkg.com/heroicons@2.0.16/24/outline/plus.svg" alt="add" style={iconImgStyle} />
            </button>
            {rows && rows.length >= 0 && (
                <>
                <button
                    type="submit"
                    disabled={submitting || (!canEdit && !canEditAndApply)}
                    style={(submitting || (!canEdit && !canEditAndApply)) ? submitDisabledStyle : submitStyle}
                    >
                    {submitting ? "Submitting…" : (canEditAndApply ? "Apply & Save" : "Submit")}
                </button>
                </>
            ) }
        </div>
      </form>
    </div>
  );
}
